import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const CONSENT_VALUE =
  'Immediate access requested and withdrawal right waiver acknowledged';

const CONSENT_VERSION = '2026-09-v1';

const CONSENT_ATTRIBUTE = 'Digital content consent';
const CONSENT_VERSION_ATTRIBUTE = 'Digital content consent version';
const CONSENT_DATE_ATTRIBUTE = 'Digital content consent accepted at';

const consentState = {
  active: false,
  acceptedAt: '',
};

let hydrationPromise = null;

const hydrateConsentState = () => {
  if (hydrationPromise) return hydrationPromise;

  hydrationPromise = fetch(
    `${window.Shopify.routes.root}cart.js`,
    {
      headers: {
        Accept: 'application/json',
      },
    }
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error('Failed to read cart consent state');
      }

      return response.json();
    })
    .then((cart) => {
      const attributes = cart.attributes ?? {};

      consentState.active =
        attributes[CONSENT_ATTRIBUTE] === CONSENT_VALUE;

      consentState.acceptedAt =
        attributes[CONSENT_DATE_ATTRIBUTE] ?? '';
    })
    .catch((error) => {
      console.warn(
        '[digital-consent] Failed to hydrate consent state:',
        error
      );
    });

  return hydrationPromise;
};

class DigitalConsentComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      'change',
      this.#handleChange
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    hydrateConsentState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener(
      'change',
      this.#handleChange
    );

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );
  }

  #handleChange = async (event) => {
    const checkbox = event.target;

    if (
      !(checkbox instanceof HTMLInputElement) ||
      !checkbox.classList.contains(
        'digital-consent__checkbox'
      )
    ) {
      return;
    }

    const checked = checkbox.checked;

    const previousState = {
      active: consentState.active,
      acceptedAt: consentState.acceptedAt,
    };

    const acceptedAt = checked
      ? new Date().toISOString()
      : '';

    const checkoutButtons =
      document.querySelectorAll(
        '.cart__checkout-button'
      );

    const acceleratedCheckoutButtons =
      document.querySelectorAll(
        '.additional-checkout-buttons'
      );

    const consentCheckboxes =
      document.querySelectorAll(
        '.digital-consent__checkbox'
      );

    const errorMessages =
      document.querySelectorAll(
        '.digital-consent__error'
      );

    if (!checked) {
      checkoutButtons.forEach((button) => {
        button.disabled = true;
      });

      acceleratedCheckoutButtons.forEach(
        (container) => {
          container.hidden = true;
        }
      );
    }

    consentCheckboxes.forEach((input) => {
      input.disabled = true;
    });

    try {
      const response = await fetch(
        `${window.Shopify.routes.root}cart/update.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            attributes: {
              [CONSENT_ATTRIBUTE]:
                checked ? CONSENT_VALUE : '',

              [CONSENT_VERSION_ATTRIBUTE]:
                checked ? CONSENT_VERSION : '',

              [CONSENT_DATE_ATTRIBUTE]:
                acceptedAt,
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          'Failed to save digital content consent'
        );
      }

      consentState.active = checked;
      consentState.acceptedAt = acceptedAt;

      document
        .querySelectorAll(
          'digital-consent-component'
        )
        .forEach((component) => {
          component.dataset.consentGiven =
            checked ? 'true' : 'false';
        });

      consentCheckboxes.forEach((input) => {
        input.checked = checked;
      });

      checkoutButtons.forEach((button) => {
        button.disabled = !checked;
      });

      acceleratedCheckoutButtons.forEach(
        (container) => {
          container.hidden = !checked;
        }
      );

      errorMessages.forEach((message) => {
        message.hidden = true;
      });
    } catch (error) {
      consentState.active =
        previousState.active;

      consentState.acceptedAt =
        previousState.acceptedAt;

      console.warn(
        '[digital-consent] Failed to update cart attribute:',
        error
      );

      consentCheckboxes.forEach((input) => {
        input.checked = !checked;
      });

      checkoutButtons.forEach((button) => {
        button.disabled = checked;
      });

      acceleratedCheckoutButtons.forEach(
        (container) => {
          container.hidden = checked;
        }
      );

      errorMessages.forEach((message) => {
        message.hidden = false;
      });
    } finally {
      consentCheckboxes.forEach((input) => {
        input.disabled = false;
      });
    }
  };

  #handleCartLinesUpdate = (event) => {
    const updateComplete =
      event.promise ?? Promise.resolve();

    updateComplete
      .then(() => {
        requestAnimationFrame(async () => {
          const components =
            document.querySelectorAll(
              'digital-consent-component'
            );

          const requiresConsent =
            Array.from(components).some(
              (component) =>
                component.dataset
                  .requiresConsent === 'true'
            );

          if (!requiresConsent) {
            if (consentState.active) {
              await this.#clearStaleConsent();
            }

            return;
          }

          if (consentState.active) {
            await this.#restoreConsent();
          }
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[digital-consent] Cart update event rejected:',
            error
          );
        }
      });
  };

  #restoreConsent = async () => {
    if (!consentState.acceptedAt) {
      consentState.acceptedAt =
        new Date().toISOString();
    }

    try {
      const response = await fetch(
        `${window.Shopify.routes.root}cart/update.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            attributes: {
              [CONSENT_ATTRIBUTE]:
                CONSENT_VALUE,

              [CONSENT_VERSION_ATTRIBUTE]:
                CONSENT_VERSION,

              [CONSENT_DATE_ATTRIBUTE]:
                consentState.acceptedAt,
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          'Failed to restore digital content consent'
        );
      }

      document
        .querySelectorAll(
          'digital-consent-component'
        )
        .forEach((component) => {
          component.dataset.consentGiven =
            'true';
        });

      document
        .querySelectorAll(
          '.digital-consent__checkbox'
        )
        .forEach((input) => {
          input.checked = true;
        });

      document
        .querySelectorAll(
          '.cart__checkout-button'
        )
        .forEach((button) => {
          button.disabled = false;
        });

      document
        .querySelectorAll(
          '.additional-checkout-buttons'
        )
        .forEach((container) => {
          container.hidden = false;
        });
    } catch (error) {
      console.warn(
        '[digital-consent] Failed to restore consent attributes:',
        error
      );

      consentState.active = false;
      consentState.acceptedAt = '';

      document
        .querySelectorAll(
          'digital-consent-component'
        )
        .forEach((component) => {
          component.dataset.consentGiven =
            'false';
        });

      document
        .querySelectorAll(
          '.digital-consent__checkbox'
        )
        .forEach((input) => {
          input.checked = false;
        });

      document
        .querySelectorAll(
          '.cart__checkout-button'
        )
        .forEach((button) => {
          button.disabled = true;
        });

      document
        .querySelectorAll(
          '.additional-checkout-buttons'
        )
        .forEach((container) => {
          container.hidden = true;
        });
    }
  };

  #clearStaleConsent = async () => {
    try {
      const response = await fetch(
        `${window.Shopify.routes.root}cart/update.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            attributes: {
              [CONSENT_ATTRIBUTE]: '',
              [CONSENT_VERSION_ATTRIBUTE]: '',
              [CONSENT_DATE_ATTRIBUTE]: '',
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          'Failed to clear digital content consent'
        );
      }

      consentState.active = false;
      consentState.acceptedAt = '';

      document
        .querySelectorAll(
          'digital-consent-component'
        )
        .forEach((component) => {
          component.dataset.consentGiven =
            'false';
        });
    } catch (error) {
      console.warn(
        '[digital-consent] Failed to clear stale cart attribute:',
        error
      );
    }
  };
}

if (
  !customElements.get(
    'digital-consent-component'
  )
) {
  customElements.define(
    'digital-consent-component',
    DigitalConsentComponent
  );
}